import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, } from "@angular/forms";
import { DatePipe } from "@angular/common";
import { BehaviorSubject, Subscription, of, Observable, forkJoin } from "rxjs"
import { tap, catchError, switchMap, map, filter } from "rxjs/operators";
import * as jp from 'jsonpath';
import { GridOptions, ColDef, GridApi } from "ag-grid-community";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { GridFormatterService } from "../../../../common/services/grid-formatter.service";
import { DateService } from "../../../../common/services/date.service";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { AdhocReportsService } from "../../../services/adhoc-reports-service";
import { FinancialComplianceProjectionRequest } from "../../../models/adhoc-reports.model";
import { OrganizationComplianceDetails } from '../../../../common/models/organization.model';
import { InsuranceDataListItem } from "../../../models/adhoc-reports.model";
import { AgGridBooleanFilterComponent, IBooleanFilterParams } from "../../../../common/components/ag-grid/filters/boolean-filter.component";
import { OrganizationService } from "../../../../common/services/organization.service";
import { OrganizationStub } from "../../../../common/models/organization.model";
import { CheckboxRendererComponent } from '../../../../common/components/ag-grid/checkbox-renderer.component';

@Component({
    selector: 'app-insurance-compliance-by-date-report',
    templateUrl: './insurance-compliance-by-date-report.component.html',
    styleUrls: ['./insurance-compliance-by-date-report.component.scss'],
    standalone: false
})
export class InsuranceComplianceByDateReportComponent implements OnInit, OnDestroy {


    _booleanfilterParams = { isInServerSideFilterMode: false, } as IBooleanFilterParams;

    data: OrganizationComplianceDetails[];


    private _dataLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    get dataLoading(): boolean {
        return this._dataLoading.value;
    }


    get excelStyles(): any[] {
        if (this.formatter) return this.formatter.excelStyles;
        return [];
    }
    forceRefreshHelp: string = "For better performance, insurance details are loaded only on the initial load of the report.  Check the 'Reload Insurance Details' box to force the insurance details to refresh when running a new projection.";
    form: FormGroup;

    gridOptions: GridOptions;
	gridApi?: GridApi;
    initialLoad: boolean = true;

    private insuranceData: Map<string, InsuranceDataListItem[]> = null;

    loadingDialog: Dialog = new Dialog("Please wait for current report to finish loading before running another projection.", [new DialogButton("OK", false, () => { return false; })]);

    nextExpirationDates: Map<string, Date> = new Map<string, Date>();
    notReadyDialog: Dialog = new Dialog("Report must finish loading before it can be exported.", [new DialogButton("OK", false, () => { return false; })]);

    organizationData: OrganizationStub[];

    subheader: SubHeader = new SubHeader({ title: "Insurance Compliance - Future Date", buttons: [{ name: "Export", method: () => this.export() }] });

    subscription: Subscription = new Subscription();

    constructor(private builder: FormBuilder, private subheaderService: SubHeaderService, private formatter: GridFormatterService, private dateService: DateService,
        private dialogService: DynamicDialogService, private reportService: AdhocReportsService, private datePipe: DatePipe, private organizationService: OrganizationService) {
        
    }

    ngOnInit() {
        this.subheaderService.updateSubHeader(this.subheader);
        this.form = this.buildForm();
        this.gridOptions = this.defineGridOptions();
        this.runReport();
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    buildForm(): FormGroup {        
        return this.builder.group({
            calculationDate: this.builder.group({ datePicker: [{ value: null, disabled: false }] }),
            forceRefreshInsurance: [{ value: false, disabled: false }]
        });
    }

    buildRequest(): FinancialComplianceProjectionRequest {
        let date = this.form.get('calculationDate').get('datePicker').value;       
        let projectionDate = date != null ? date : new Date();
        projectionDate.setHours(0, 0, 0, 0);
        return {
            projectionDate: projectionDate
        } as FinancialComplianceProjectionRequest;
    }

    calculationDateChanged(date: Date): void {

    }

    defineColumns(): ColDef[] { 
        let defs: ColDef[] = [
            {
                headerClass: "wrapped",
                headerName: "Company Name",
                headerTooltip: "Company Name",
                field: "name",
                tooltipField: "name",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 200
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "RICS Owner #",
                headerTooltip: "RICS Owner #",
                field: "orgId",
                tooltipField: "orgId",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 100
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "RBDMS Owner #",
                headerTooltip: "RBDMS Owner #",
                field: "historicalOwnerNumber",
                tooltipField: "historicalOwnerNumber",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 100
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Owner Type",
                headerTooltip: "Owner Type",
                valueGetter: this.formatOwnerType(),
                tooltipValueGetter: this.formatOwnerType(),
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 125
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Amount Required",
                headerTooltip: "Amount Required",
                field: "requiredInsurance",
                tooltipValueGetter: this.formatter.formatCurrency("requiredInsurance", "USD", "symbol", "1.2-2"),
                valueFormatter: this.formatter.formatCurrency("requiredInsurance", "USD", "symbol", "1.2-2"),
                filter: "agNumberColumnFilter",
                filterParams: this.formatter.defaultNumberFilterParams
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Amount Provided",
                headerTooltip: "Amount Provided",
                field: "insuranceProvided",
                tooltipValueGetter: this.formatter.formatCurrency("insuranceProvided", "USD", "symbol", "1.2-2"),
                valueFormatter: this.formatter.formatCurrency("insuranceProvided", "USD", "symbol", "1.2-2"),
                filter: "agNumberColumnFilter",
                filterParams: this.formatter.defaultNumberFilterParams
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "In Compliance",
                headerTooltip: "In Compliance",
                field: "isInsuranceCompliant",
                tooltipField: "isInsuranceCompliant",
                 cellRendererParams: {
                    action: () => { return false; },
                    field: "isInsuranceCompliant",
                    description: "In Compliance",
                    isVisible: () => { return true; },
                    isEnabled: () => { return false; },
                    renderAsYesNo: true
                },
                cellRenderer: CheckboxRendererComponent,
                tooltipValueGetter: this.formatter.formatBooleanToYesNo("isInsuranceCompliant"),
				filter: AgGridBooleanFilterComponent,
                filterParams: this._booleanfilterParams,
                minWidth: 135,
                maxWidth: 135
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Next Expiration Date",
                headerTooltip: "Next Expiration Date",
                valueGetter: this.getExpirationDate(false),
                valueFormatter: this.getExpirationDate(true),
                tooltipValueGetter: this.getExpirationDate(true),
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams,
                minWidth: 100

            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Primary Contact Name",
                headerTooltip: "Primary Contact Name",
                valueGetter: this.getContactField("$.organizationPrimaryContact"),
                tooltipValueGetter: this.getContactField("$.organizationPrimaryContact"),
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Address 1",
                headerTooltip: "Address 1",
                valueGetter: this.getOrganizationField("street"),
                tooltipValueGetter: this.getOrganizationField("street"),
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 200
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Address 2",
                headerTooltip: "Address 2",
                valueGetter: this.getOrganizationField("suite"),
                tooltipValueGetter: this.getOrganizationField("suite"),
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "City",
                headerTooltip: "City",
                valueGetter: this.getOrganizationField("city"),
                tooltipValueGetter: this.getOrganizationField("city"),
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "State",
                headerTooltip: "State",
                valueGetter: this.getOrganizationField("stateCode"),
                tooltipValueGetter: this.getOrganizationField("stateCode"),
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Zip Code",
                headerTooltip: "Zip Code",
                valueGetter: this.getOrganizationField("zipCode"),
                tooltipValueGetter: this.getOrganizationField("zipCode"),
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "E-mail",
                headerTooltip: "E-mail",
                valueGetter: this.getOrganizationField("email"),
                tooltipValueGetter: this.getOrganizationField("email"),
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 110
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Phone",
                headerTooltip: "Phone",
                valueGetter: this.getOrganizationField("phone"),
                tooltipValueGetter: this.getOrganizationField("phone"),
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 110
            } as ColDef,
        ];
        return defs;
    }

    defineGridOptions(): GridOptions {
        let options = this.formatter.BuildDefaultGridOptionsWithColumns(this.defineColumns(), false, true, true, true);
        options.groupDisplayType = "groupRows";
        options.headerHeight = 50;
        options.getRowClass = (params: any) => {
            if (params.data.isCompliant == false) return 'red';
        };
		options.pagination = false;
		options.onGridReady = (params) => { this.gridApi = params.api; };
        return options;
    }

    export(): void {
        if (this.dataLoading == false) {
            let projectionDate = this.form.get('calculationDate').get('datePicker').value;
            let fileName = `CompanyInsuranceReportAsOf_${this.datePipe.transform(projectionDate, "yyyy-MM-dd")}`;
            let sheetName = `Company Insurance Reporrt`;
            this.formatter.exportDataToExcel(fileName, sheetName, this.gridApi);
        } else {
            this.notReadyDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.notReadyDialog);
        }
    }

    formatOwnerType(): any {
        return (obj: any) => {
            let ownership: string = "";
            if (obj.data) {
                if (obj.data.isBrineHauler) ownership = "Brine Hauler";
                if (obj.data.isWellOwner) {
                    if (ownership.length > 0) ownership = `${ownership}, Well Owner`;
                    else ownership = "Well Owner";
                }
            }
            if (ownership.length === 0) ownership = "N/A";
            return ownership;
        }
    }

    getContactField(field: string): any {
        return (obj: any) => {
            let value: string = "";
            let organizationId = obj.data["orgId"];
            if (this.insuranceData.has(organizationId) == false) organizationId = obj.data["historicalOwnerNumber"];
            if (organizationId != null && organizationId.length > 0 && this.insuranceData.has(organizationId)) {
                let list = this.insuranceData.get(organizationId);
                let insurance: InsuranceDataListItem = null;                
                if (field.includes('organizationEmail')) {
                    insurance = list.find(i => i.ricsOrganizationId === organizationId && i.organizationEmail != null);
                } else {
                    insurance = list.find(i => i.ricsOrganizationId === organizationId);
                }
                if (insurance != null) value = jp.value(insurance, field);
            }
            return value;
        }
    }

    getExpirationDate(formatted: boolean): any {
        return (obj: any) => {
            let expiryDate: Date = null;
            if (obj && obj.data) {
                let organizationId = obj.data["orgId"];
                if (organizationId != null && organizationId.length > 0) {
                    if (this.nextExpirationDates.has(organizationId)) {
                        expiryDate = this.nextExpirationDates.get(organizationId);
                    }
                }
            }
            if (formatted) {
                if (expiryDate != null) {
                    return this.datePipe.transform(expiryDate, "MM/dd/yyyy", '+0000');
                }
                return "No Active Insurance on File";
            }
            return expiryDate;
        }
    }

    getOrganizationField(field: any): any {
        return (obj: any) => {
            let value: string = "";
            if (obj && obj.data) {
                let organizationId = obj.data["orgId"];
                if (organizationId != null && organizationId.length > 0) {
                    let stub: OrganizationStub = this.organizationData.find(o => o.id === organizationId);
                    if (stub != null) {
                        if (field === 'phone') {
                            value = stub['phone'];
                        } else if (field === 'email') {
                            value = stub['email'];
                            if (value == null || value.length === 0) {
                                if (this.insuranceData.has(organizationId)) {
                                    let insurances = this.insuranceData.get(organizationId);
                                    let withEmail = insurances.find(i => i.organizationEmail != null && i.organizationEmail.length > 0);
                                    if (withEmail != null) value = withEmail.organizationEmail; // in this case, e-mail could be the contact's e-mail if the organization doesn't have one itself
                                }
                            }
                        } else {
                            let address = stub["address"];
                            if (address != null) value = address[field];
                        }
                    }
                }
            }           
            return value;
        }
    }

    handleError(error: any): void {
        console.error(error);
        this.dialogService.showError();
    }

    runReport(): void {
        if (this.dataLoading && !this.initialLoad) {
            this.loadingDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.loadingDialog);
            return;
        }
        this.initialLoad = false;
        this.form.disable();
        let dateRequest: FinancialComplianceProjectionRequest = this.buildRequest();
        let insuranceObs$: Observable<Map<string, InsuranceDataListItem[]>>;
        let organizationObs$: Observable<OrganizationStub[]> = (this.organizationData != null) ? of(this.organizationData) : this.organizationService.getAllExternalOrganizations();
        let forceRefresh : boolean = <boolean>this.form.get('forceRefreshInsurance').value;
        if (!forceRefresh && this.insuranceData != null) insuranceObs$ = of(this.insuranceData); //insurance data is not date dependent, only needs to be loaded on the initial load
        else insuranceObs$ = this.reportService.getInsuranceDataListReport().pipe(
            map((data: InsuranceDataListItem[]) => {
                return data.filter(d => d.ricsOrganizationId != null && d.ricsOrganizationId.length > 0
                    && d.insurancePolicyType != null && (d.insurancePolicyType.toLocaleLowerCase() != 'auto' && d.insurancePolicyType.toLocaleLowerCase() != 'automobile'));
                    //auto policies don't count toward compliance so don't include them for expiration date purposes
            }),
            map((data: InsuranceDataListItem[]) => {
                let tempInsuranceData = new Map<string, InsuranceDataListItem[]>();
                data.forEach(d => {
                    if (tempInsuranceData.has(d.ricsOrganizationId)) {
                        let temp = tempInsuranceData.get(d.ricsOrganizationId);
                        temp.push(d);
                        tempInsuranceData.set(d.ricsOrganizationId, temp);
                    } else tempInsuranceData.set(d.ricsOrganizationId, [d]);
                });
                return tempInsuranceData;
            })
        );
        let obs$: Observable<OrganizationComplianceDetails[]> = forkJoin([insuranceObs$, organizationObs$]).pipe(
            tap(([insuranceMap, orgData]) => {
                this.insuranceData = insuranceMap;
                for (let [key, value] of insuranceMap) {
                    let dates = value.filter(v => v != null && v.expirationDate != null && new Date(v.expirationDate) >= dateRequest.projectionDate).sort((a, b) => {
                        if (a.expirationDate > b.expirationDate) return 1;
                        if (a.expirationDate < b.expirationDate) return -1;
                        return 0;
                    });
                    if (dates.length > 0) {
                        this.nextExpirationDates.set(key, dates[0].expirationDate);
                    }
                }
                this.organizationData = orgData;
            }),
            switchMap(() => {
                return this.reportService.projectFinancialCompliance(this.buildRequest())
            }),
            catchError((error) => {
                this.handleError(error);
                return of([]);
            })
        );
        if (this.gridApi) this.gridApi.setGridOption("loading", true)
        let subs$ = obs$.subscribe({
            next: (rows: OrganizationComplianceDetails[]) => {
                this.data = rows;
                if (this.gridApi) this.gridApi.setGridOption("loading", false);
                this.form.enable();
                this._dataLoading.next(false);
            },
            error: (error) => {
                this.handleError(error);
            }
        });
        this.subscription.add(subs$);
    }
}
